'use client';

import { Details } from '@ria-client/tw-shared-ui';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { CITATION_LIST_ID } from './constants';
import { useCitationContext } from './context';
import { useHashScrollOnMount } from './use-hash-scroll-on-mount';

export interface CitationListAccordionProps {
  summary: ReactNode;
  children: ReactNode;
}

export function CitationListAccordion({ summary, children }: CitationListAccordionProps) {
  const {
    citationListExpanded,
    setCitationListExpanded,
    shouldScrollToCitations,
    setShouldScrollToCitations,
  } = useCitationContext();
  const accordionRef = useRef<HTMLDivElement>(null);

  const handleExpand = useCallback(() => {
    setCitationListExpanded(true);
  }, [setCitationListExpanded]);

  useHashScrollOnMount({ onExpand: handleExpand, hashKey: CITATION_LIST_ID });

  useEffect(() => {
    if (shouldScrollToCitations && window.location.hash === `#${CITATION_LIST_ID}`) {
      accordionRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
      setShouldScrollToCitations(false);
    }
  }, [citationListExpanded, setShouldScrollToCitations, shouldScrollToCitations]);

  return (
    <div id={CITATION_LIST_ID} ref={accordionRef}>
      <Details
        variant="simple"
        detailsProps={{
          open: citationListExpanded,
          onToggle: event => setCitationListExpanded(event.currentTarget.open),
        }}
        SummaryComponent={
          <span className="flex items-center gap-2">
            {summary}
            <ChevronDown aria-hidden className="expand-icon size-5" />
          </span>
        }
        summaryComponentProps={{ className: 'p-3' }}
      >
        <div className="pb-3 pl-3">{children}</div>
      </Details>
    </div>
  );
}
